import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { BaseUrl } from "../../BaseUrl";

export default function EmailNotificationsSettings({ doctor }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    // Appointments & Patients
    newAppointments: true,
    appointmentReminders: true,
    appointmentCancellations: true,
    newPatients: true,

    // Messages
    messages: true,

    // Staff & Professionals
    staffUpdates: true,
    professionalInvitations: true,

    // Orders & Payments
    newOrders: true,
    paymentReceived: true,

    // System
    systemUpdates: true,
    securityAlerts: true,

    // Digest frequency
    digestFrequency: "daily",
  });

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(
          `${BaseUrl}/doctor/settings/email-notifications`,
          {
            withCredentials: true,
          }
        );
        if (response.data?.success && response.data?.data) {
          setSettings((prev) => ({
            ...prev,
            ...response.data.data,
          }));
        }
      } catch (error) {
        console.error("Error fetching email notification settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleToggle = async (key) => {
    const newValue = !settings[key];
    setSettings((prev) => ({
      ...prev,
      [key]: newValue,
    }));

    try {
      await axios.put(
        `${BaseUrl}/doctor/settings/email-notifications/update`,
        { [key]: newValue },
        { withCredentials: true }
      );
      toast.success("Setting updated");
    } catch (error) {
      // Revert on error
      setSettings((prev) => ({
        ...prev,
        [key]: !newValue,
      }));
      toast.error(error.response?.data?.message || "Failed to update setting");
    }
  };

  const handleFrequencyChange = async (frequency) => {
    const oldValue = settings.digestFrequency;
    setSettings((prev) => ({
      ...prev,
      digestFrequency: frequency,
    }));

    try {
      await axios.put(
        `${BaseUrl}/doctor/settings/email-notifications/update`,
        { digestFrequency: frequency },
        { withCredentials: true }
      );
      toast.success("Digest frequency updated");
    } catch (error) {
      // Revert on error
      setSettings((prev) => ({
        ...prev,
        digestFrequency: oldValue,
      }));
      toast.error(
        error.response?.data?.message || "Failed to update frequency"
      );
    }
  };

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className='mb-5'>
      <div className='flex items-center justify-between'>
        <div>
          <span className='font-medium text-gray-900'>{label}</span>
          {description && (
            <p className='text-sm text-gray-500'>{description}</p>
          )}
        </div>
        <label className='relative inline-flex cursor-pointer items-center'>
          <input
            type='checkbox'
            checked={checked}
            onChange={onChange}
            className='peer sr-only'
          />
          <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
        </label>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className='max-w-3xl animate-pulse'>
        <div className='mb-6 h-8 w-1/3 rounded bg-gray-200'></div>
        <div className='space-y-4'>
          <div className='h-16 rounded bg-gray-200'></div>
          <div className='h-16 rounded bg-gray-200'></div>
          <div className='h-16 rounded bg-gray-200'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-3xl'>
      <h2 className='mb-6 border-b border-gray-200 pb-3 text-2xl font-semibold text-gray-900'>
        Email & Notifications
      </h2>

      {/* Appointments & Patients Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>
          Appointments & Patients
        </h3>
        <ToggleSwitch
          checked={settings.newAppointments}
          onChange={() => handleToggle("newAppointments")}
          label='New Appointments'
          description='Notify me when a new appointment is booked'
        />
        <ToggleSwitch
          checked={settings.appointmentReminders}
          onChange={() => handleToggle("appointmentReminders")}
          label='Appointment Reminders'
          description='Send reminders before upcoming appointments'
        />
        <ToggleSwitch
          checked={settings.appointmentCancellations}
          onChange={() => handleToggle("appointmentCancellations")}
          label='Appointment Cancellations'
          description='Notify me when an appointment is cancelled'
        />
        <ToggleSwitch
          checked={settings.newPatients}
          onChange={() => handleToggle("newPatients")}
          label='New Patients'
          description='Notify me when a new patient registers'
        />
      </div>

      {/* Messages Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>Messages</h3>
        <ToggleSwitch
          checked={settings.messages}
          onChange={() => handleToggle("messages")}
          label='Direct Messages'
          description='Notify me when I receive a new message'
        />
      </div>

      {/* Staff & Professionals Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>
          Staff & Professionals
        </h3>
        <ToggleSwitch
          checked={settings.staffUpdates}
          onChange={() => handleToggle("staffUpdates")}
          label='Staff Updates'
          description='Notify me about staff activity and updates'
        />
        <ToggleSwitch
          checked={settings.professionalInvitations}
          onChange={() => handleToggle("professionalInvitations")}
          label='Professional Invitations'
          description='Notify me when professionals accept or decline invitations'
        />
      </div>

      {/* Orders & Payments Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>
          Orders & Payments
        </h3>
        <ToggleSwitch
          checked={settings.newOrders}
          onChange={() => handleToggle("newOrders")}
          label='New Orders'
          description='Notify me when a new order is placed'
        />
        <ToggleSwitch
          checked={settings.paymentReceived}
          onChange={() => handleToggle("paymentReceived")}
          label='Payment Received'
          description='Notify me when a payment is received'
        />
      </div>

      {/* System Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>System</h3>
        <ToggleSwitch
          checked={settings.systemUpdates}
          onChange={() => handleToggle("systemUpdates")}
          label='System Updates'
          description='Notify me about platform updates and new features'
        />
        <ToggleSwitch
          checked={settings.securityAlerts}
          onChange={() => handleToggle("securityAlerts")}
          label='Security Alerts'
          description='Important security notifications (recommended to keep on)'
        />
      </div>

      {/* Email Digest Frequency */}
      <div className='mb-8'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>
          Email Digest Frequency
        </h3>
        <p className='mb-4 text-sm text-gray-500'>
          How often would you like to receive email summaries?
        </p>
        <div className='space-y-3'>
          <label className='flex cursor-pointer items-center'>
            <input
              type='radio'
              name='digestFrequency'
              value='asAvailable'
              checked={settings.digestFrequency === "asAvailable"}
              onChange={() => handleFrequencyChange("asAvailable")}
              className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <div className='ml-3'>
              <span className='text-gray-900'>Immediately</span>
              <p className='text-sm text-gray-500'>
                Receive notifications as they happen
              </p>
            </div>
          </label>
          <label className='flex cursor-pointer items-center'>
            <input
              type='radio'
              name='digestFrequency'
              value='daily'
              checked={settings.digestFrequency === "daily"}
              onChange={() => handleFrequencyChange("daily")}
              className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <div className='ml-3'>
              <span className='text-gray-900'>Daily Digest</span>
              <p className='text-sm text-gray-500'>
                Receive a summary once per day
              </p>
            </div>
          </label>
          <label className='flex cursor-pointer items-center'>
            <input
              type='radio'
              name='digestFrequency'
              value='weekly'
              checked={settings.digestFrequency === "weekly"}
              onChange={() => handleFrequencyChange("weekly")}
              className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <div className='ml-3'>
              <span className='text-gray-900'>Weekly Digest</span>
              <p className='text-sm text-gray-500'>
                Receive a summary once per week
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}
